/**
 * @file Generating image urls
 *
 *
 *
 * @author Torbjørn Høvde :)
 * @version 1.0
 *
 * @example
 *
 */

import { BGCOLOR } from '@/Scripts/constants';
import imgSrcMixin from '@/Scripts/lib/mixins/imgSrcMixin';

export default {
  mixins: [imgSrcMixin],
  data () {
    return {
      psimgScale: 6,
      focalWidth: 320,
      srcScaleSteps: [190, 220, 275, 320, 500, 764, 1000, 1324, 1569, 1956, 2138],
      psimg: {
        loaded: false,
        width: 0,
        height: 0,
        src: 'notloaded',
        mssrc: ''
      },
      focalZoomLevels: [1, 1.25, 1.5, 1.75, 2, 2.5, 2.75, 3, 3.5, 4]
    };
  },

  props: {
    /**
         * Object with data for generating URL.
         */
    imageObject: {
      type: Object,
      required: true,
      default: () => {
        return {
          Url: '',
          Path: '',
          Version: ''
        };
      }
    },
    /**
         * Ratio to use for mobile pictures
         */
    focalRatio: {
      type: Number,
      default: 0.75
    },
    /**
         * Background color to be added to transparent images and padding
         */
    backgroundColor: {
      type: String,
      default: BGCOLOR.DEFAULT,
      required: false
    },

    /**
         * Width of image, 0 = auto
         */
    width: {
      type: Number,
      required: false
    },

    /**
         * Height of image, 0 = auto
         */
    height: {
      type: Number,
      required: false
    },

    maxWidth: {
      type: Number,
      default: 0
    },
    maxHeight: {
      type: Number,
      default: 0
    },
    isFocal: {
      type: Boolean,
      default: false
    }

  },
  computed: {
    updateSrc () {
      if (typeof this.imageObject === 'undefined' || this.imageObject === null) {
        return '';
      }
      const isSrcSetSupported = ('sizes' in document.createElement('img'));
      if (isSrcSetSupported) {
        return this.makeUrl(this.width, this.height);
      } else {
        return this.addSize(this.srcScale(this.srcScaleSteps.length - 1));
      }
    },
    updateSrcset () {
      if (typeof this.imageObject === 'undefined' || this.imageObject === null) {
        return '';
      }
      // https://css-tricks.com/responsive-images-youre-just-changing-resolutions-use-srcset/
      // https://www.responsivebreakpoints.com/

      let str = '';
      for (let i = 0; i < this.srcScaleSteps.length; i++) {
        str += `${this.addSize(this.srcScale(i))} ${this.srcScaleSteps[i]}w`;
        if (i < this.srcScaleSteps.length - 1) {
          str += ',';
        }
      }
      // const s = this.addSize(this.srcScale.s);
      // const m = this.addSize(this.srcScale.m);
      // const l = this.addSize(this.srcScale.l);
      // const xl = this.addSize(this.srcScale.xl);
      // return `${s} 320w, ${m} 500w, ${l} 1000w, ${xl} 2000w`;
      return str;
    },
    srcWithoutLazy () {
      if (this.index === 0 || !this.lazy) {
        return this.updateSrc;
      }
      return this.updateSrc;
      // return ''; is suppose to return empty, but wont update src tag of image
    },
    srcsetWithoutLazy () {
      if (this.index === 0 || !this.lazy) {
        return this.updateSrcset;
      }
      return this.updateSrcset;
      // return ''; is suppose to return empty, but wont update src tag of image
    },
    focalHeight () {
      return this.focalWidth * this.focalRatio;
    }

  },
  methods: {
    /**
         * Generating Cloudinary image URL
         *
         * @param {width} num Width`
         * @param {Number} num Height'
         * @param {bgcolor} str Hex code or transparent'
         * @param {crop} str Cloudinary crop setting'
         * @param {quality} str Cloudinary qualtity setting'
         * @param {filetype} str jpg or png'
         */
    makeUrl: function (width, height, bgcolor = this.backgroundColor, crop = 'c_pad', quality = 'q_auto', filetype = 'jpg', focalOn = false, focalSize = 1) {
      if (typeof this.imageObject === 'undefined' || this.imageObject === null) {
        console.warn('ImageObject is not correct formated: Object = null', this.imageObject);
        return '';
      }
      if (this.imageObject.Path === null) {
        console.warn('ImageObject is not correct formated: Path = null', this.imageObject);
        return encodeURI(this.imageObject.Url);
      }
      return this.imgSrcBuilder(this.imageObject, width, height, bgcolor, {
        crop,
        quality,
        filetype,
        focalOn,
        focalSize
      });
    },
    addSize: function (scale = 1, bgcolor = this.backgroundColor) {
      const w = Math.round(this.width * scale);
      const h = Math.round(this.height * scale);
      return this.makeUrl(w, h, bgcolor);
    },
    srcScale (step) {
      return this.srcScaleSteps[step] / 320;
    },
    focal (size = 1) {
      let str = '';
      if (
        this.imageObject &&
                this.imageObject.FocalPointX &&
                this.imageObject.FocalPointX > 0
      ) {
        str = `x_${this.imageObject.FocalPointX},y_${this.imageObject.FocalPointY},w_${Math.round(this.focalWidth * this.focalZoomLevels[this.imageObject.FocalZoomLevel - 1])},h_${Math.round(this.focalHeight * this.focalZoomLevels[this.imageObject.FocalZoomLevel - 1])},c_crop,g_xy_center/w_${320 * size}/`;
      }
      // console.log(str);
      return str;
    },
    enablePhotoSwipe () {
      const img = new Image();
      img.onload = () => {
        // creating data to photoswipe (lightbox)
        this.psimg.width = this.maxWidth > 0 ? this.maxWidth : img.width * this.psimgScale;
        this.psimg.height = this.maxHeight > 0 ? this.maxHeight : img.height * this.psimgScale;
        this.psimg.src = this.makeUrl(
          this.psimg.width,
          this.psimg.height,
          'transparent'
        );
        this.psimg.mssrc = this.makeUrl(img.width, img.height, 'transparent');
        this.psimg.loaded = true;
      };
      img.src = this.addSize(1);
    }

  }
};
